/**
 * Add Fee Modal
 *
 * Modal for selecting an Encompass fee and configuring pricing (fixed, tiered, or percentage).
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, Plus, Trash2, Loader2, DollarSign, Layers, Percent } from 'lucide-react';
import { toast } from 'sonner';
import { EncompassItemizationFee } from '@/types/encompass-fees';
import { PricingType, CreateFeeScheduleInput } from '@/types/title-escrow';
import { encompassFeeService } from '@/services/encompassFeeService';
import { titleEscrowService } from '@/services/titleEscrowService';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('AddFeeModal', 'title-escrow');

// Format number with commas (1000000 -> "1,000,000")
const formatWithCommas = (value: string): string => {
  const num = value.replace(/,/g, '');
  if (!num || isNaN(Number(num))) {
return value;
}
  // Handle decimals properly
  const parts = num.split('.');
  parts[0] = Number(parts[0]).toLocaleString('en-US');
  return parts.join('.');
};

// Parse formatted string to number string (remove commas)
const parseCommas = (value: string): string => {
  return value.replace(/,/g, '');
};

interface TierInput {
  id: string;
  minLoanAmount: string;
  maxLoanAmount: string;
  amount: string;
}

interface AddFeeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  existingFeeIds: string[];
  onFeeAdded: () => void;
}

export const AddFeeModal: React.FC<AddFeeModalProps> = ({
  open,
  onOpenChange,
  companyId,
  existingFeeIds,
  onFeeAdded
}) => {
  // Fee selection
  const [encompassFees, setEncompassFees] = useState<EncompassItemizationFee[]>([]);
  const [loadingFees, setLoadingFees] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFee, setSelectedFee] = useState<EncompassItemizationFee | null>(null);

  // Pricing configuration
  const [pricingType, setPricingType] = useState<PricingType>('fixed');
  const [fixedAmount, setFixedAmount] = useState('');
  const [tiers, setTiers] = useState<TierInput[]>([
    { id: '1', minLoanAmount: '0', maxLoanAmount: '500000', amount: '' }
  ]);
  const [percentageRate, setPercentageRate] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');

  // Conditions
  const [isConditional, setIsConditional] = useState(false);
  const [conditionDescription, setConditionDescription] = useState('');

  // Saving
  const [saving, setSaving] = useState(false);

  // Load Encompass fees
  useEffect(() => {
    if (open) {
      loadEncompassFees();
    }
  }, [open]);

  const loadEncompassFees = async () => {
    try {
      setLoadingFees(true);
      const fees = await encompassFeeService.getFees();
      setEncompassFees(fees);
    } catch (error) {
      log.error('Failed to load Encompass fees', {}, error as Error);
      toast.error('Failed to load fees');
    } finally {
      setLoadingFees(false);
    }
  };

  const resetForm = () => {
    setSelectedFee(null);
    setPricingType('fixed');
    setFixedAmount('');
    setTiers([{ id: '1', minLoanAmount: '0', maxLoanAmount: '500000', amount: '' }]);
    setPercentageRate('');
    setMinAmount('');
    setMaxAmount('');
    setIsConditional(false);
    setConditionDescription('');
    setSearchQuery('');
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const addTier = () => {
    const lastTier = tiers[tiers.length - 1];
    const lastMax = lastTier ? parseFloat(parseCommas(lastTier.maxLoanAmount)) : 0;
    const newMinAmount = !isNaN(lastMax) && lastMax > 0 ? String(lastMax + 1) : '0';
    setTiers([
      ...tiers,
      {
        id: String(Date.now()),
        minLoanAmount: newMinAmount,
        maxLoanAmount: '',
        amount: ''
      }
    ]);
  };

  const removeTier = (id: string) => {
    if (tiers.length > 1) {
      setTiers(tiers.filter((t) => t.id !== id));
    }
  };

  const updateTier = (id: string, field: keyof TierInput, value: string) => {
    setTiers((prev) => {
      const updated = prev.map((t) => (t.id === id ? { ...t, [field]: value } : t));

      // If maxLoanAmount changed, auto-cascade to update next tier's minLoanAmount
      if (field === 'maxLoanAmount') {
        const idx = updated.findIndex((t) => t.id === id);
        if (idx >= 0 && idx < updated.length - 1 && value) {
          const parsedMax = parseFloat(parseCommas(value));
          if (!isNaN(parsedMax)) {
            const nextMin = String(parsedMax + 1);
            updated[idx + 1] = { ...updated[idx + 1], minLoanAmount: nextMin };
          }
        }
      }

      return updated;
    });
  };

  const handleSave = async () => {
    if (!selectedFee) {
      toast.error('Please select a fee');
      return;
    }

    try {
      setSaving(true);

      if (pricingType === 'fixed') {
        const amount = parseFloat(fixedAmount);
        if (isNaN(amount) || amount < 0) {
          toast.error('Please enter a valid amount');
          return;
        }

        await titleEscrowService.addFeeToSchedule({
          companyId,
          feeId: selectedFee.id,
          pricingType: 'fixed',
          amount,
          isConditional,
          conditionDescription: isConditional ? conditionDescription : undefined
        });
      } else if (pricingType === 'tiered') {
        const validTiers = tiers
          .filter((t) => t.amount)
          .map((t) => ({
            minLoanAmount: parseFloat(t.minLoanAmount) || 0,
            maxLoanAmount: t.maxLoanAmount ? parseFloat(t.maxLoanAmount) : null,
            amount: parseFloat(t.amount)
          }));

        if (validTiers.length === 0) {
          toast.error('Please enter at least one tier');
          return;
        }

        await titleEscrowService.addTieredFee(companyId, selectedFee.id, validTiers, {
          isConditional,
          conditionDescription: isConditional ? conditionDescription : undefined
        });
      } else if (pricingType === 'percentage') {
        const rate = parseFloat(percentageRate);
        if (isNaN(rate) || rate < 0) {
          toast.error('Please enter a valid percentage');
          return;
        }

        await titleEscrowService.addFeeToSchedule({
          companyId,
          feeId: selectedFee.id,
          pricingType: 'percentage',
          percentageRate: rate / 100, // Convert from percentage to decimal
          minAmount: minAmount ? parseFloat(minAmount) : undefined,
          maxAmount: maxAmount ? parseFloat(maxAmount) : undefined,
          isConditional,
          conditionDescription: isConditional ? conditionDescription : undefined
        });
      }

      toast.success('Fee added to schedule');
      onFeeAdded();
      handleClose();
    } catch (error) {
      log.error('Failed to add fee', {}, error as Error);
      toast.error('Failed to add fee');
    } finally {
      setSaving(false);
    }
  };

  // Filter fees based on search and exclude already added
  const filteredFees = encompassFees.filter(
    (fee) =>
      !existingFeeIds.includes(fee.id) &&
      (fee.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        fee.encompassFeeId.includes(searchQuery) ||
        (fee.ucdFee && fee.ucdFee.toLowerCase().includes(searchQuery.toLowerCase())))
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Add Fee to Schedule</DialogTitle>
          <DialogDescription>
            Select a fee from the Encompass fee library and configure its pricing.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          {!selectedFee ? (
            // Fee Selection Step
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search Encompass fees..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  autoFocus
                />
              </div>

              <ScrollArea className="h-[300px] border rounded-md">
                {loadingFees ? (
                  <div className="p-4 space-y-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Skeleton className="h-4 w-4 rounded-full" />
                        <Skeleton className="h-4 w-48" />
                      </div>
                    ))}
                  </div>
                ) : filteredFees.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <p>No matching fees found</p>
                    {existingFeeIds.length > 0 && (
                      <p className="text-sm mt-1">
                        ({existingFeeIds.length} fee{existingFeeIds.length !== 1 ? 's' : ''} already
                        added)
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="p-2">
                    {filteredFees.map((fee) => (
                      <button
                        key={fee.id}
                        className="w-full text-left px-3 py-2 rounded hover:bg-muted flex items-start gap-3"
                        onClick={() => setSelectedFee(fee)}
                      >
                        <div className="flex-1">
                          <p className="font-medium">{fee.name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-muted-foreground">
                              ID: {fee.encompassFeeId}
                            </span>
                            {fee.ucdFee && (
                              <span className="text-xs text-muted-foreground">
                                UCD: {fee.ucdFee}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {fee.sections.map((section) => (
                            <Badge key={section} variant="secondary" className="text-xs">
                              {section.replace('Section', '')}
                            </Badge>
                          ))}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          ) : (
            // Pricing Configuration Step
            <div className="space-y-6">
              {/* Selected Fee Display */}
              <div className="p-3 bg-muted rounded-md">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{selectedFee.name}</p>
                    <p className="text-sm text-muted-foreground">
                      ID: {selectedFee.encompassFeeId}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedFee(null)}>
                    Change
                  </Button>
                </div>
              </div>

              {/* Pricing Type Selection */}
              <div className="space-y-3">
                <Label>Pricing Type</Label>
                <RadioGroup
                  value={pricingType}
                  onValueChange={(v) => setPricingType(v as PricingType)}
                  className="grid grid-cols-3 gap-4"
                >
                  <div>
                    <RadioGroupItem value="fixed" id="fixed" className="peer sr-only" />
                    <Label
                      htmlFor="fixed"
                      className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                    >
                      <DollarSign className="mb-2 h-6 w-6" />
                      <span className="text-sm font-medium">Fixed Amount</span>
                    </Label>
                  </div>
                  <div>
                    <RadioGroupItem value="tiered" id="tiered" className="peer sr-only" />
                    <Label
                      htmlFor="tiered"
                      className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                    >
                      <Layers className="mb-2 h-6 w-6" />
                      <span className="text-sm font-medium">Tiered</span>
                    </Label>
                  </div>
                  <div>
                    <RadioGroupItem value="percentage" id="percentage" className="peer sr-only" />
                    <Label
                      htmlFor="percentage"
                      className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                    >
                      <Percent className="mb-2 h-6 w-6" />
                      <span className="text-sm font-medium">Percentage</span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Fixed Amount Input */}
              {pricingType === 'fixed' && (
                <div className="space-y-2">
                  <Label htmlFor="fixedAmount">Amount</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
                      $
                    </span>
                    <Input
                      id="fixedAmount"
                      type="number"
                      step="0.01"
                      min="0"
                      value={fixedAmount}
                      onChange={(e) => setFixedAmount(e.target.value)}
                      className="pl-7"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              )}

              {/* Tiered Pricing Input */}
              {pricingType === 'tiered' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Loan Amount Tiers</Label>
                    <Button variant="outline" size="sm" onClick={addTier}>
                      <Plus className="w-3 h-3 mr-1" />
                      Add Tier
                    </Button>
                  </div>
                  <div className="max-h-[200px] overflow-y-auto pr-1">
                    <div className="space-y-2">
                      {tiers.map((tier, idx) => (
                        <div key={tier.id} className="flex items-center gap-2">
                          <div className="flex-1 grid grid-cols-3 gap-2">
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-xs text-muted-foreground">
                                $
                              </span>
                              <Input
                                type="text"
                                inputMode="numeric"
                                value={formatWithCommas(tier.minLoanAmount)}
                                onChange={(e) =>
                                  updateTier(tier.id, 'minLoanAmount', parseCommas(e.target.value))
                                }
                                className="pl-5 text-sm"
                                placeholder="Min"
                              />
                            </div>
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-xs text-muted-foreground">
                                $
                              </span>
                              <Input
                                type="text"
                                inputMode="numeric"
                                value={formatWithCommas(tier.maxLoanAmount)}
                                onChange={(e) =>
                                  updateTier(tier.id, 'maxLoanAmount', parseCommas(e.target.value))
                                }
                                className="pl-5 text-sm"
                                placeholder="Max (no limit)"
                              />
                            </div>
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-xs text-muted-foreground">
                                $
                              </span>
                              <Input
                                type="text"
                                inputMode="decimal"
                                value={formatWithCommas(tier.amount)}
                                onChange={(e) =>
                                  updateTier(tier.id, 'amount', parseCommas(e.target.value))
                                }
                                className="pl-5 text-sm"
                                placeholder="Amount"
                              />
                            </div>
                          </div>
                          {tiers.length > 1 && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => removeTier(tier.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Percentage Input */}
              {pricingType === 'percentage' && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="percentageRate">Percentage Rate</Label>
                    <div className="relative w-32">
                      <Input
                        id="percentageRate"
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={percentageRate}
                        onChange={(e) => setPercentageRate(e.target.value)}
                        placeholder="0.00"
                      />
                      <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
                        %
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="minAmount">Minimum Fee</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
                          $
                        </span>
                        <Input
                          id="minAmount"
                          type="number"
                          step="0.01"
                          min="0"
                          value={minAmount}
                          onChange={(e) => setMinAmount(e.target.value)}
                          className="pl-7"
                          placeholder="No minimum"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="maxAmount">Maximum Fee (Cap)</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
                          $
                        </span>
                        <Input
                          id="maxAmount"
                          type="number"
                          step="0.01"
                          min="0"
                          value={maxAmount}
                          onChange={(e) => setMaxAmount(e.target.value)}
                          className="pl-7"
                          placeholder="No maximum"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Conditional Fee */}
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="isConditional"
                    checked={isConditional}
                    onCheckedChange={(checked) => setIsConditional(!!checked)}
                  />
                  <Label htmlFor="isConditional" className="cursor-pointer">
                    This fee is conditional (only charged when applicable)
                  </Label>
                </div>
                {isConditional && (
                  <Input
                    placeholder="e.g., 'if credit card payment', 'per deed needed'"
                    value={conditionDescription}
                    onChange={(e) => setConditionDescription(e.target.value)}
                  />
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !selectedFee}>
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Plus className="w-4 h-4 mr-2" />
            )}
            Add to Schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddFeeModal;
